import { DetailsHonoraryNoteActionTypes, DetailsHonoraryNoteState, HonoraryNote } from './types';
import { Reducer } from 'redux';
import { ActionType } from 'typesafe-actions';
import * as honoraryNoteActions from './actions';
import { validateHonoraryNote } from '../../../business/logic/honorary-note-logic';
import { BusinessHonoraryNote } from '../../../business/models/honorarynote-business.model';
import { BusinessValidation } from '../../../business/models/policy-business.model';
export type HonoraryNoteAction = ActionType<typeof honoraryNoteActions>;

const initialState: DetailsHonoraryNoteState = {
  selectedHonoraryNote: {
    honoraryNoteId: 0,
    caseCode: '',
    instructionId: 0,
    honoraryNoteLines: [],
    honoraryReference: '',
    locked: false,
    messageDate: '',
    createdOn: '',
    createdByFirstName: '',
    createdByLastName: '',
    approved: true,
    structuredMessage: ''
  },
  honoraryNotes: [],
  lineTypeValues: [],
  unitTypeValues: [],
  vatPercentageValues: [],
  validations : [],
};

const reducer: Reducer<DetailsHonoraryNoteState, HonoraryNoteAction> = (state = initialState, action): DetailsHonoraryNoteState => {
  switch (action.type) {
    case DetailsHonoraryNoteActionTypes.FETCH_HONORARY_NOTES_SUCCESS: {
      const honoraryNotes = action.payload.map(x => ({
        ...x,
        messageDate: x.messageDate ? new Date(x.messageDate).toISOString() : '',
        createdOn: x.createdOn ? new Date(x.createdOn).toISOString() : '',
        instructionId: x.instructionId,
      }));
      return { ...state, honoraryNotes: [...honoraryNotes] };
    }
    case DetailsHonoraryNoteActionTypes.FETCH_HONORARY_NOTE_TYPECODES_SUCCESS: {
      return { ...state, ...action.payload };
    }
    case DetailsHonoraryNoteActionTypes.RESET_STATE: {
      return initialState;
    }
    case DetailsHonoraryNoteActionTypes.NEW_HONORARY_NOTE: {
      return { ...state, selectedHonoraryNote: { ...initialState.selectedHonoraryNote, instructionId: action.payload.instructionId } };
    }
    case DetailsHonoraryNoteActionTypes.SAVE_HONORARY_NOTE_SUCCESS: {
      const honoraryNoteResponse: HonoraryNote = {
        ...action.payload.honoraryNote,
        messageDate: action.payload.honoraryNote.messageDate
          ? new Date(action.payload.honoraryNote.messageDate).toISOString()
          : '',
        createdOn: action.payload.honoraryNote.createdOn
          ? new Date(action.payload.honoraryNote.createdOn).toISOString()
          : '',
      };

      const honoraryNotes = state.honoraryNotes.map(note =>
        note.honoraryNoteId === action.payload.honoraryNote.honoraryNoteId
          ? { ...note, ...honoraryNoteResponse }
          : note
      );

      return {
        ...state,
        honoraryNotes: honoraryNotes.some(x => x.honoraryNoteId === action.payload.honoraryNote.honoraryNoteId)
          ? honoraryNotes
          : [...honoraryNotes, honoraryNoteResponse],
        selectedHonoraryNote: honoraryNoteResponse,
      };
    }
    case DetailsHonoraryNoteActionTypes.DELETE_HONORARY_NOTE_SUCCESS: {
      const honoraryNotes = state.honoraryNotes.filter(
        note => note.honoraryNoteId !== action.payload.honoraryNoteId
      );

      const selectedHonoraryNote =
        honoraryNotes.length > 0 ? honoraryNotes[0] : initialState.selectedHonoraryNote;

      return { ...state, honoraryNotes: honoraryNotes, selectedHonoraryNote: selectedHonoraryNote };
    }
    case DetailsHonoraryNoteActionTypes.ADD_HONORARY_NOTE_LINE_SUCCESS: {
      const honoraryNotes = state.honoraryNotes.map(note =>
        note.honoraryNoteId === action.payload.honoraryNoteLine.honoraryNoteId
          ? {
              ...note,
              honoraryNoteLines: [...note.honoraryNoteLines, action.payload.honoraryNoteLine],
            }
          : note
      );

      const selectedHonoraryNote: HonoraryNote = honoraryNotes.find(
        note => note.honoraryNoteId === state.selectedHonoraryNote.honoraryNoteId
      ) || initialState.selectedHonoraryNote;

      return { ...state, honoraryNotes: honoraryNotes, selectedHonoraryNote: selectedHonoraryNote };
    }
    case DetailsHonoraryNoteActionTypes.DELETE_HONORARY_NOTE_LINE_SUCCESS: {
      const honoraryNotes = state.honoraryNotes.map(note =>
        note.honoraryNoteId === state.selectedHonoraryNote.honoraryNoteId
          ? {
              ...note,
              honoraryNoteLines: note.honoraryNoteLines.filter(
                line => line.honoraryNoteLineId !== action.payload.honoraryNoteLineId
              ),
            }
          : note
      );

      const selectedHonoraryNote: HonoraryNote = honoraryNotes.find(
        note => note.honoraryNoteId === state.selectedHonoraryNote.honoraryNoteId
      ) || initialState.selectedHonoraryNote;

      return { ...state, honoraryNotes: honoraryNotes, selectedHonoraryNote: selectedHonoraryNote };
    }
    case DetailsHonoraryNoteActionTypes.SELECT_HONORARY_NOTE: {
      const selectedHonoraryNote =
        state.honoraryNotes.find(x => x.honoraryNoteId === action.payload.honoraryNoteId) ||
        initialState.selectedHonoraryNote;

      return { ...state, selectedHonoraryNote: selectedHonoraryNote };
    }
    case DetailsHonoraryNoteActionTypes.UPDATE_HONORARY_REFERENCE: {
      return { ...state, selectedHonoraryNote: { ...state.selectedHonoraryNote, honoraryReference: action.payload.honoraryReference } };
    }
    case DetailsHonoraryNoteActionTypes.UPDATE_HONORARY_STRUCTURED_MESSAGE: {
      const changedHonoraryNote: HonoraryNote = { ...state.selectedHonoraryNote, structuredMessage: action.payload.structuredMessage };
      const newValidations: BusinessValidation[] = validateHonoraryNote(changedHonoraryNote as BusinessHonoraryNote);
      let stateValidationErrors = state.validations;

      newValidations.filter(el => el.hasError === false).forEach(elem => {
        stateValidationErrors = stateValidationErrors.filter(item => item.businessValidationType !== elem.businessValidationType);
      });
      stateValidationErrors.push(...newValidations);

      return { ...state, selectedHonoraryNote: changedHonoraryNote, validations: stateValidationErrors };
    }
    case DetailsHonoraryNoteActionTypes.FETCH_HONORARY_NOTES_FAIL:
    default: {
      return state;
    }
  }
};

export {
  reducer as detailsHonoraryNoteReducer,
  initialState as detailsHonoraryNoteInitialState,
};
